import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, MotionConfig, animate, useInView, useScroll, useTransform } from 'framer-motion';
import { usePrefersReducedMotion, fadeUpVariants, staggerContainer } from './motion';

// ── Drone SVG with spinning rotors ──────────────────────────────────────────
function DroneSVG() {
  const rotor = (cx, cy, rev) => (
    <g style={{ transformBox: 'fill-box', transformOrigin: 'center',
        animation: `rotorSpin ${rev ? '0.09s' : '0.07s'} linear infinite`,
        animationDirection: rev ? 'reverse' : 'normal' }}>
      <ellipse cx={cx} cy={cy} rx="40" ry="7"  fill="rgba(0,212,255,0.22)" stroke="rgba(0,212,255,0.4)" strokeWidth="0.5"/>
      <ellipse cx={cx} cy={cy} rx="40" ry="4"  fill="rgba(0,212,255,0.12)"/>
      <ellipse cx={cx} cy={cy} rx="40" ry="1.5" fill="rgba(0,212,255,0.08)"/>
    </g>
  );
  return (
    <svg width="300" height="300" viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow2" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <radialGradient id="bodyG" cx="40%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#1a3a62"/>
          <stop offset="100%" stopColor="#040c1a"/>
        </radialGradient>
        <radialGradient id="lensG" cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#0f2a4a"/>
          <stop offset="100%" stopColor="#010610"/>
        </radialGradient>
      </defs>
      {[[150,150,68,68],[150,150,232,68],[150,150,68,232],[150,150,232,232]].map(([x1,y1,x2,y2],i)=>(
        <g key={i}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#112030" strokeWidth="8" strokeLinecap="round"/>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#00d4ff" strokeWidth="1" strokeLinecap="round" opacity="0.18"/>
        </g>
      ))}
      {[[68,68],[232,68],[68,232],[232,232]].map(([cx,cy],i)=>(
        <g key={i} filter="url(#glow2)">
          <circle cx={cx} cy={cy} r="24" fill="#07101f" stroke="#00d4ff" strokeWidth="1.5"/>
          <circle cx={cx} cy={cy} r="17" fill="#0a1828" stroke="#193655" strokeWidth="1"/>
          <circle cx={cx} cy={cy} r="7"  fill="#0e1f36"/>
          <circle cx={cx} cy={cy} r="3"  fill="#162844"/>
        </g>
      ))}
      {rotor(68,  68,  false)}
      {rotor(232, 68,  true)}
      {rotor(68,  232, true)}
      {rotor(232, 232, false)}
      <rect x="110" y="110" width="80" height="80" rx="13" fill="url(#bodyG)" stroke="#00d4ff" strokeWidth="1.8" filter="url(#glow2)"/>
      <rect x="118" y="118" width="64" height="64" rx="8" fill="none" stroke="#1a3a5c" strokeWidth="0.8"/>
      {[136,143,150].map(y=>(
        <g key={y}>
          <line x1="111" y1={y} x2="119" y2={y} stroke="#00d4ff" strokeWidth="1" opacity="0.28"/>
          <line x1="181" y1={y} x2="189" y2={y} stroke="#00d4ff" strokeWidth="1" opacity="0.28"/>
        </g>
      ))}
      <circle cx="150" cy="150" r="22" fill="#050d1c" stroke="#00d4ff" strokeWidth="1.3" filter="url(#glow2)"/>
      <circle cx="150" cy="150" r="15" fill="url(#lensG)"/>
      <circle cx="150" cy="150" r="9"  fill="#030810"/>
      <circle cx="150" cy="150" r="5"  fill="#00d4ff" opacity="0.1"/>
      <circle cx="144" cy="144" r="3.5" fill="rgba(255,255,255,0.55)"/>
      <circle cx="144" cy="144" r="1.8" fill="rgba(255,255,255,0.9)"/>
      {[[68,68,'#00d4ff',0],[232,68,'#00d4ff',0.45],[68,232,'#ef4444',0.9],[232,232,'#ef4444',1.35]].map(([cx,cy,c,d])=>(
        <circle key={`${cx}${cy}`} cx={cx} cy={cy} r="4.5" fill={c}
          style={{ filter:`drop-shadow(0 0 5px ${c})`, animation:`ledPulse 2s ease-in-out infinite`, animationDelay:`${d}s` }}/>
      ))}
    </svg>
  );
}

// ── Floating particles (memoized so positions don't regenerate) ─────────────
function ParticleField() {
  const reducedMotion = usePrefersReducedMotion();
  const particles = useMemo(() =>
    Array.from({ length: 22 }, (_, i) => ({
      id: i,
      size: Math.random() * 2.2 + 1,
      x: Math.random() * 100,
      y: Math.random() * 100,
      dur: 8 + Math.random() * 14,
      delay: Math.random() * 10,
      color: i % 3 === 0 ? 'rgba(124,58,237,0.7)' : 'rgba(0,212,255,0.7)',
      shadow: i % 3 === 0 ? '0 0 7px rgba(124,58,237,0.9)' : '0 0 7px rgba(0,212,255,0.9)',
    })), []);

  if (reducedMotion) return null;
  return (
    <div className="aurora-wrap">
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          width: p.size + 'px', height: p.size + 'px',
          borderRadius: '50%',
          left: p.x + '%', top: p.y + '%',
          background: p.color, boxShadow: p.shadow,
          animation: `particleFloat ${p.dur}s ease-in-out infinite`,
          animationDelay: `${p.delay}s`,
          pointerEvents: 'none',
        }}/>
      ))}
    </div>
  );
}

// ── Animated counter stat ────────────────────────────────────────────────────
function AnimatedStat({ value, suffix = '', label }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [display, setDisplay] = useState(0);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!isInView) return;
    if (reducedMotion) { setDisplay(value); return; }
    const controls = animate(0, value, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: v => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [isInView, reducedMotion, value]);

  return (
    <motion.div
      ref={ref}
      className="glass-card stat-glass"
      variants={fadeUpVariants}
      initial="hidden"
      animate={isInView ? 'show' : 'hidden'}
    >
      <span className="stat-num">{display}{suffix}</span>
      <span className="stat-label">{label}</span>
    </motion.div>
  );
}

// ── Join / Lead capture section ──────────────────────────────────────────────
const API_URL_MARKETING = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function JoinSection() {
  const [tab, setTab] = React.useState('client');
  const [form, setForm] = React.useState({ name: '', email: '', phone: '' });
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    try {
      await fetch(API_URL_MARKETING + '/marketing/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          message: tab === 'pilot' ? 'טייס מוסמך — רוצה להצטרף ל-SkySnap' : 'לקוח — רוצה לצלם עם רחפן',
          source: tab === 'pilot' ? 'pilot_signup' : 'client_signup',
        }),
      });
      setSent(true);
    } catch { setSent(true); }
    setLoading(false);
  };

  return (
    <motion.section
      className="join-v2"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Ambient glow */}
      <div style={{
        position: 'absolute', width: '600px', height: '600px', borderRadius: '50%',
        top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 68%)',
        filter: 'blur(48px)', pointerEvents: 'none',
      }}/>

      <div className="join-v2-inner">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ color: '#fff', fontSize: '2.1rem', fontWeight: 900, marginBottom: 10, fontFamily: 'var(--f-display)', lineHeight: 1.2 }}
        >
          בואו לעולם של <span className="gradient-text">SkySnap</span>
        </motion.h2>
        <p style={{ color: 'rgba(255,255,255,0.62)', marginBottom: 36, fontSize: '1.05rem', lineHeight: 1.6 }}>
          בין אם אתם צריכים צילום רחפן או רוצים להרוויח כטייס — מתחילים כאן
        </p>

        {/* Toggle tabs */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 32 }}>
          {[
            { id: 'client', label: 'רוצה לצלם',      icon: '📸', active: '#06b6d4', tc: '#fff' },
            { id: 'pilot',  label: 'אני טייס מוסמך', icon: '🚁', active: '#f59e0b', tc: '#000' },
          ].map(t => (
            <motion.button
              key={t.id}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => { setTab(t.id); setSent(false); }}
              style={{
                padding: '11px 28px', borderRadius: 30, cursor: 'pointer',
                fontWeight: 700, fontSize: '0.95rem', fontFamily: 'var(--f-body)',
                border: `1px solid ${tab === t.id ? t.active : 'rgba(255,255,255,0.12)'}`,
                background: tab === t.id ? t.active : 'rgba(255,255,255,0.06)',
                color: tab === t.id ? t.tc : 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(12px)',
                transition: 'all 0.25s ease',
              }}>
              {t.icon} {t.label}
            </motion.button>
          ))}
        </div>

        {/* Pilot promo banner */}
        {tab === 'pilot' && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35 }}
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              borderRadius: 16, padding: '20px 28px', marginBottom: 24,
              position: 'relative', overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', top: -20, right: -20, fontSize: 80, opacity: 0.13, lineHeight: 1 }}>🚁</div>
            <div style={{ fontSize: '0.73rem', fontWeight: 700, letterSpacing: 2, color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', marginBottom: 6 }}>מבצע השקה</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#000', marginBottom: 4 }}>20 הטייסים הראשונים</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#000' }}>מקבלים מנוי שנתי בחינם לחלוטין</div>
            <div style={{ fontSize: '0.82rem', color: 'rgba(0,0,0,0.58)', marginTop: 6 }}>הירשמו עכשיו ואנחנו ניצור קשר לפני שהמבצע נגמר</div>
          </motion.div>
        )}

        {!sent ? (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
            {tab === 'client' && (
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.87rem', marginBottom: 4 }}>
                השאירו פרטים — נחבר אתכם לטייס מוסמך באזורכם
              </p>
            )}
            <input
              placeholder="שם מלא"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="join-input-v2"
            />
            <input
              type="email" placeholder="כתובת אימייל *" required
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="join-input-v2"
            />
            <input
              type="tel" placeholder="טלפון (אופציונלי)"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="join-input-v2"
            />
            <motion.button
              type="submit" disabled={loading}
              whileHover={!loading ? { scale: 1.03, y: -2 } : {}}
              whileTap={!loading ? { scale: 0.97 } : {}}
              style={{
                marginTop: 8, padding: '14px 44px', borderRadius: 30, border: 'none',
                cursor: loading ? 'default' : 'pointer', fontWeight: 700, fontSize: '1rem',
                fontFamily: 'var(--f-body)',
                background: tab === 'client' ? '#06b6d4' : '#f59e0b',
                color: tab === 'client' ? '#fff' : '#000',
                opacity: loading ? 0.7 : 1,
                boxShadow: tab === 'client'
                  ? '0 4px 22px rgba(6,182,212,0.32)'
                  : '0 4px 22px rgba(245,158,11,0.32)',
              }}>
              {loading ? '...' : tab === 'client' ? 'אני רוצה לצלם עם רחפן' : 'אני טייס — הצטרף'}
            </motion.button>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{
              background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.28)',
              borderRadius: 18, padding: '36px 28px', color: '#fff', backdropFilter: 'blur(16px)',
            }}
          >
            <div style={{ fontSize: '2.8rem', marginBottom: 14 }}>✅</div>
            <h3 style={{ margin: '0 0 8px', fontFamily: 'var(--f-display)', fontSize: '1.25rem' }}>
              {tab === 'client' ? 'מעולה! נחזור אליך בקרוב' : 'ברוכים הבאים למשפחת SkySnap!'}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.62)', margin: 0, fontSize: '0.92rem' }}>
              {tab === 'pilot' ? 'קיבלנו את הפרטים שלך — תהיו מהראשונים כשנשיק 🚁' : 'ניצור איתך קשר בהקדם לתיאום הצילום'}
            </p>
          </motion.div>
        )}
      </div>
    </motion.section>
  );
}

// ── SVG icon helpers ─────────────────────────────────────────────────────────
const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
  </svg>
);
const LightningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"/>
  </svg>
);
const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316Z"/>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"/>
  </svg>
);
const MoneyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>
  </svg>
);
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/>
  </svg>
);
const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/>
  </svg>
);

// ── Home Page ────────────────────────────────────────────────────────────────
function Home() {
  const heroRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const droneY       = useTransform(scrollYProgress, [0, 1], reducedMotion ? [0, 0]   : [0, -60]);
  const droneOpacity = useTransform(scrollYProgress, [0, 1], reducedMotion ? [1, 1]   : [1, 0.4]);

  const steps = [
    { num: '01', title: 'מצאו צלם', desc: 'גלשו במפה, ראו פורטפוליו, בחרו צלם זמין באזורכם.', Icon: MapPinIcon },
    { num: '02', title: 'הזמינו ושלמו', desc: 'בחרו תאריך ושעה, שלמו בצורה מאובטחת, וקבלו אישור.', Icon: CalendarIcon },
    { num: '03', title: 'קבלו תוצאות', desc: 'תוך 24 שעות תקבלו לינק להורדת הצילומים הערוכים.', Icon: DownloadIcon },
  ];

  const features = [
    { Icon: MapPinIcon,   title: 'מפה בזמן אמת',     desc: 'ראו איפה יש צלמים זמינים כרגע על מפת ישראל',              span: 2 },
    { Icon: LightningIcon, title: 'הזמנה מהירה',      desc: 'הזמינו צלם תוך חצי שעה וקבלו וידאו תוך 24 שעות',          span: 1 },
    { Icon: CameraIcon,   title: 'פורטפוליו',         desc: 'צפו בעבודות קודמות של הצלמים ובחרו את המתאים לכם',         span: 1 },
    { Icon: MoneyIcon,    title: 'מחירים שקופים',     desc: 'מחירים ברורים ללא הפתעות — כולל עמלה שקופה למערכת',        span: 1 },
    { Icon: ShieldIcon,   title: 'טייסים מוסמכים',   desc: 'כל הטייסים ברשימה מוסמכים ורשומים — תעופו בשקט',           span: 1 },
    { Icon: StarIcon,     title: 'ביקורות וידוגמות', desc: 'קראו ביקורות, ראו עבודות קודמות, ובחרו בביטחון מלא',        span: 2 },
  ];

  return (
    <MotionConfig reducedMotion="user">
    <div>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="hero" ref={heroRef} style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Aurora orbs */}
        <div className="aurora-wrap">
          <div className="aurora-orb aurora-orb-1"/>
          <div className="aurora-orb aurora-orb-2"/>
          <div className="aurora-orb aurora-orb-3"/>
        </div>

        {/* Floating particles */}
        <ParticleField/>

        {/* Scan line */}
        <div className="hero-scan"/>

        {/* Drone scene (parallax on scroll) */}
        <motion.div className="drone-scene" style={{ y: droneY, opacity: droneOpacity, position: 'relative', zIndex: 2 }}>
          <div className="drone-corner-tr"/><div className="drone-corner-bl"/>
          <div className="drone-glow"/>
          <div className="hud-item" style={{ top:'6%', left:'4%', animationDelay:'0.5s' }}>
            <span className="hud-label">ALT</span>120m
          </div>
          <div className="hud-item" style={{ top:'6%', right:'4%', animationDelay:'0.7s' }}>
            <span className="hud-label">BATT</span>87%
          </div>
          <div className="hud-item" style={{ bottom:'8%', left:'4%', animationDelay:'0.9s' }}>
            <span className="hud-label">SPD</span>0 km/h
          </div>
          <div className="hud-item" style={{ bottom:'8%', right:'4%', animationDelay:'1.1s' }}>
            <span className="hud-label">GPS</span>12 SAT
          </div>
          <div className="drone-svg-wrap"><DroneSVG/></div>
          <div className="drone-thrust"/>
        </motion.div>

        {/* Live badge */}
        <div className="hero-badge" style={{ position: 'relative', zIndex: 3 }}>
          <span className="hero-badge-dot"/>
          מערכת פעילה · 24/7
        </div>

        {/* Headline — with gradient on "Snap" */}
        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.72, ease: [0.16, 1, 0.3, 1] }}
          style={{ position: 'relative', zIndex: 3 }}
        >
          Sky <span className="gradient-text">Snap</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.72, delay: 0.14, ease: [0.16, 1, 0.3, 1] }}
          style={{ position: 'relative', zIndex: 3 }}
        >
          מצאו צלם רחפנים זמין בקרבתכם תוך שעה
        </motion.p>

        <motion.div
          className="hero-ctas"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.72, delay: 0.27, ease: [0.16, 1, 0.3, 1] }}
          style={{ position: 'relative', zIndex: 3 }}
        >
          <Link to="/map" className="btn btn-primary">חפשו צלם קרוב</Link>
          <Link to="/feed" className="btn btn-outline btn-glow">ראו פורטפוליו</Link>
        </motion.div>
      </div>

      {/* ── Stats (glass cards grid) ───────────────────────────────────────── */}
      <div className="stats-v2">
        <AnimatedStat value={50} suffix="+" label="צלמי רחפן"/>
        <AnimatedStat value={20} suffix="+" label="ערים ברחבי הארץ"/>
        <AnimatedStat value={24} suffix="H" label="זמן אספקת הוידאו"/>
      </div>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <div className="how-section">
        <span className="section-tag-v2">איך זה עובד</span>
        <h2 className="section-title">תוך 3 צעדים פשוטים</h2>
        <p className="section-subtitle">
          מהזמנה ועד קבלת הצילומים — תהליך ישיר, שקוף ומהיר.
        </p>

        <motion.div
          className="steps-v2"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          {steps.map(({ num, title, desc, Icon }) => (
            <motion.div
              key={num}
              className="glass-card step-card-v2"
              variants={fadeUpVariants}
              whileTap={{ scale: 0.98 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div className="step-num-circle">{num}</div>
                <div className="feature-icon-v2" style={{ marginBottom: 0 }}>
                  <Icon/>
                </div>
              </div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* ── Features bento grid ───────────────────────────────────────────── */}
      <div style={{ paddingBottom: 80 }}>
        <div style={{ textAlign: 'center', marginBottom: 44, padding: '0 24px' }}>
          <span className="section-tag-v2">יתרונות</span>
          <h2 className="section-title" style={{ marginTop: 8 }}>כל מה שצריך במקום אחד</h2>
          <p className="section-subtitle" style={{ marginTop: 0 }}>
            פלטפורמה שמחברת לקוחות וטייסים — מהיר, מאובטח, מקצועי
          </p>
        </div>

        <motion.div
          className="bento-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
        >
          {features.map(({ Icon, title, desc, span }, i) => (
            <motion.div
              key={title}
              className={`glass-card bento-card${span === 2 ? ' span-2' : ''}`}
              variants={fadeUpVariants}
              whileTap={{ scale: 0.98 }}
            >
              <div className="feature-icon-v2"><Icon/></div>
              <h3 className="feature-v2-title">{title}</h3>
              <p className="feature-v2-desc">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* ── Join / lead capture ───────────────────────────────────────────── */}
      <JoinSection/>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="site-footer">
        <div className="site-footer-inner">
          <Link to="/" className="footer-brand" dir="ltr">Sky<strong>Snap</strong></Link>
          <div className="footer-links">
            <Link to="/privacy">מדיניות פרטיות</Link>
            <a href="mailto:support@skysnap.co.il">support@skysnap.co.il</a>
          </div>
        </div>
        <p className="footer-copy">
          © 2026 SkySnap · בהתאם לחוק הגנת הצרכן, תשמ"א-1981 וחוק הגנת הפרטיות, תשמ"א-1981
        </p>
      </footer>

    </div>
    </MotionConfig>
  );
}

export default Home;
